import PropTypes from 'prop-types';
import {
    onAuthStateChanged,
    signInWithEmailAndPassword,
    createUserWithEmailAndPassword,
    signOut,
    sendPasswordResetEmail,
    sendEmailVerification,
} from 'firebase/auth';
import React, {
    createContext,
    useContext,
    useEffect,
    useState,
    useCallback,
    useMemo,
    useRef,
} from 'react';

import firebase from '@/shared/services/firebase';
import logger from '@/shared/utils/logger';

const auth = firebase.auth;

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [userRole, setUserRole] = useState(null);
    const [hasProfile, setHasProfile] = useState(null);
    const [isUsernameAutoGenerated, setIsUsernameAutoGenerated] =
        useState(null);
    const [hasSeenTour, setHasSeenTour] = useState(null);
    const [tourStep, setTourStep] = useState(0);
    const [profileLoading, setProfileLoading] = useState(true);
    const [profileRefreshCallbacks, setProfileRefreshCallbacks] = useState([]);
    const profileFetchIdRef = useRef(0);

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
            setUser(firebaseUser);
            setLoading(false);
            // Reset profile state when user changes (logout, different user)
            if (!firebaseUser) {
                setUserRole(null);
                setHasProfile(null);
                setIsUsernameAutoGenerated(null);
                setHasSeenTour(null);
                setTourStep(0);
            }
        });
        return () => unsubscribe();
    }, []);

    // Fetch user profile (role, hasProfile) when authenticated + email verified.
    // Uses a fetch ID to discard results from stale requests when the user
    // changes rapidly (Task #119: race condition guard).
    const fetchProfile = useCallback(async () => {
        // Bump the fetch id on EVERY invocation, including the early-return
        // path below. Otherwise a transition to an unverified/null user (e.g.
        // logout) would not invalidate a verified user's in-flight fetch, and
        // that stale response could overwrite the just-cleared profile state
        // (Task #119: race condition guard).
        const fetchId = ++profileFetchIdRef.current;
        if (!user || !user.emailVerified) {
            setHasProfile(null);
            setUserRole(null);
            setProfileLoading(false);
            return;
        }
        setProfileLoading(true);
        try {
            // Use auth.currentUser (real Firebase User) instead of the `user`
            // state variable, which may be a plain spread object from
            // reloadCurrentUser and lacks prototype methods like getIdToken.
            const token = await auth.currentUser.getIdToken(true);
            const baseUrl = process.env.REACT_APP_API_BASE_URL || '';
            const response = await fetch(`${baseUrl}/api/users/me`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            // Discard stale response if user changed during fetch
            if (fetchId !== profileFetchIdRef.current) return;
            if (response.ok) {
                const data = await response.json();
                setHasProfile(true);
                setUserRole(data.role || 'user');
                setIsUsernameAutoGenerated(
                    data.isUsernameAutoGenerated ?? false
                );
                setHasSeenTour(data.hasSeenTour ?? false);
                setTourStep(data.tourStep ?? 0);
            } else if (response.status === 404 || response.status === 401) {
                setHasProfile(false);
                setUserRole(null);
                setIsUsernameAutoGenerated(null);
                setHasSeenTour(null);
                setTourStep(0);
            } else {
                logger.error('Profile fetch failed:', response.status);
                setHasProfile(null);
                setUserRole(null);
                setIsUsernameAutoGenerated(null);
                setHasSeenTour(null);
                setTourStep(0);
            }
        } catch (error) {
            // Discard stale error if user changed during fetch
            if (fetchId !== profileFetchIdRef.current) return;
            logger.error('Failed to fetch user profile:', error);
            setHasProfile(null);
            setUserRole(null);
            setIsUsernameAutoGenerated(null);
            setHasSeenTour(null);
            setTourStep(0);
        } finally {
            if (fetchId === profileFetchIdRef.current) {
                setProfileLoading(false);
            }
        }
    }, [user]);

    useEffect(() => {
        fetchProfile();
    }, [fetchProfile]);

    const login = useCallback(
        (email, password) => signInWithEmailAndPassword(auth, email, password),
        []
    );

    const signup = useCallback(async (email, password) => {
        const userCredential = await createUserWithEmailAndPassword(
            auth,
            email,
            password
        );

        const newUser = userCredential.user;

        // Send verification email
        await sendEmailVerification(newUser);
        await newUser.getIdToken();

        return newUser;
    }, []);

    const logout = useCallback(() => signOut(auth), []);

    const reloadCurrentUser = useCallback(async () => {
        if (auth.currentUser) {
            await auth.currentUser.reload();
            // Spread to create a new object reference so React detects the change
            setUser({ ...auth.currentUser });
        }
    }, []);

    const registerProfileRefreshCallback = useCallback((callback) => {
        setProfileRefreshCallbacks((prev) => [...prev, callback]);
        // Return unregister function
        return () => {
            setProfileRefreshCallbacks((prev) =>
                prev.filter((cb) => cb !== callback)
            );
        };
    }, []);

    const refreshProfileStatus = useCallback(() => {
        fetchProfile();
        profileRefreshCallbacks.forEach((callback) => callback());
    }, [fetchProfile, profileRefreshCallbacks]);

    // Lightweight setter for tour state — avoids full profile refetch
    // (which triggers profileLoading and unmounts protected routes).
    const updateTourState = useCallback((seen, step = 0) => {
        setHasSeenTour(seen);
        setTourStep(step);
    }, []);

    const resetPassword = useCallback(
        (email) => sendPasswordResetEmail(auth, email),
        []
    );

    // Memoize the context value to prevent unnecessary re-renders of consumers
    // when unrelated parent state changes (Task #117).
    const contextValue = useMemo(
        () => ({
            user,
            loading,
            userRole,
            hasProfile,
            isUsernameAutoGenerated,
            profileLoading,
            isAdmin: userRole === 'admin',
            hasSeenTour,
            tourStep,
            login,
            signup,
            logout,
            resetPassword,
            reloadCurrentUser,
            fetchProfile,
            registerProfileRefreshCallback,
            refreshProfileStatus,
            updateTourState,
        }),
        [
            user,
            loading,
            userRole,
            hasProfile,
            isUsernameAutoGenerated,
            profileLoading,
            hasSeenTour,
            tourStep,
            login,
            signup,
            logout,
            resetPassword,
            reloadCurrentUser,
            fetchProfile,
            registerProfileRefreshCallback,
            refreshProfileStatus,
            updateTourState,
        ]
    );

    return (
        <AuthContext.Provider value={contextValue}>
            {!loading && children}
        </AuthContext.Provider>
    );
};

AuthProvider.propTypes = {
    children: PropTypes.node.isRequired,
};

export const useAuth = () => useContext(AuthContext);
